import { capitalize } from "lodash-es";
import { FC, useMemo } from "react";

import { EngagementTableDisclaimer } from "./EngagementTableDisclaimer";

import {
  EngagementLevel,
  ExtendedUserEngagementDataType,
  UserEngagementDataType,
} from "api/api-data-types";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { IconText } from "mds/components/IconText";
import {
  SortableBody,
  SortableHeader,
  SortableTable,
  SortableTableExportButton,
  SortableTableWrapper,
} from "mds/components/SortableTable";
import { Tooltip } from "mds/components/Tooltip";
import { CircleInfoIcon, OneBarIcon, ThreeBarsIcon, TwoBarsIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentSectionTitle,
  selectHasVirtualSessionsForQualifyingSessionsInDateRange,
  selectQualifyingSessionIdsForDateRange,
  selectStudentClassSessionUserSummaries,
  selectTotalQualifyingSessionsForDateRange,
  selectUsersWhoCanParticipateInSection,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import {
  ATTENDANCE_THRESHOLD_FOR_MEDIUM_RISK,
  ATTENDANCE_THRESHOLD_FOR_RISK_INCREASE,
  MIN_QUALIFYING_SESSIONS_FOR_ATTENDANCE_RISK,
  isStudentPresent,
} from "utils/metrics-utils";
import { generateEngagementDataCSV } from "utils/reporting";
import { PROFILE_PROPERTIES, getUserFullName } from "utils/user-utils";

type SortField = "student" | "effort" | "performance" | "attendance" | "talk_time" | "risk_level";

const LevelSortValue = {
  [EngagementLevel.NONE]: 0,
  [EngagementLevel.LOW]: 1,
  [EngagementLevel.MEDIUM]: 2,
  [EngagementLevel.HIGH]: 3,
};

const getSortValue = (data: ExtendedUserEngagementDataType, field: SortField) => {
  switch (field) {
    case "student":
      return getUserFullName(data);
    case "effort":
      // -1 indicates no data (different from a percentile of 0)
      return data.effort_percentile ?? -1;
    case "performance":
      // -1 indicates no data (different from an average of 0)
      return data.performance_average ?? -1;
    case "attendance":
      return data.attendance_count ?? -1;
    case "talk_time":
      return data.avg_talk_time_minutes ?? -1;
    case "risk_level":
      return LevelSortValue[data.risk_level ?? EngagementLevel.NONE];
    default:
      return 0;
  }
};

const NotEnoughData = () => {
  return (
    <Tooltip text={t("engagement_table.not_enough_data")}>
      <CircleInfoIcon className="text-black-tint-55" />
    </Tooltip>
  );
};

const formatLevel = (level: EngagementLevel) => {
  if (level === EngagementLevel.NONE) {
    return "-";
  }
  return capitalize(level);
};

const formatEffort = (data: ExtendedUserEngagementDataType) => {
  const { effort_level, effort_percentile } = data;
  if (!effort_level || effort_level === EngagementLevel.NONE) {
    return <NotEnoughData />;
  }
  return `${formatLevel(effort_level)} (${effort_percentile?.toFixed(0)}%)`;
};

const formatPerformance = (data: ExtendedUserEngagementDataType) => {
  const { performance_level, performance_average } = data;
  if (!performance_level || performance_level === EngagementLevel.NONE) {
    return <NotEnoughData />;
  }
  return `${formatLevel(performance_level)} (${performance_average?.toFixed(1)} avg)`;
};

const riskLevelIcons: Record<EngagementLevel, React.ReactNode> = {
  [EngagementLevel.NONE]: null,
  [EngagementLevel.HIGH]: <OneBarIcon />,
  [EngagementLevel.MEDIUM]: <TwoBarsIcon />,
  [EngagementLevel.LOW]: <ThreeBarsIcon />,
};

const formatRiskLevel = (data: ExtendedUserEngagementDataType) => {
  const { risk_level } = data;
  if (!risk_level || risk_level === EngagementLevel.NONE) {
    return <NotEnoughData />;
  }
  const className =
    risk_level === EngagementLevel.HIGH
      ? "text-red-shade-20"
      : risk_level === EngagementLevel.MEDIUM
        ? "text-orange-shade-20"
        : "text-green-shade-20";
  return (
    <IconText
      className={className}
      iconStart={riskLevelIcons[risk_level]}
      text={`${formatLevel(risk_level)} Risk`}
    />
  );
};

const formatAttendance = (data: ExtendedUserEngagementDataType, totalSessions: number) => {
  const { attendance_count } = data;
  if (attendance_count === undefined || attendance_count === null) {
    return <NotEnoughData />;
  }
  return `${attendance_count} / ${totalSessions}`;
};

const formatTalkTime = (data: ExtendedUserEngagementDataType) => {
  const { avg_talk_time_minutes } = data;
  if (avg_talk_time_minutes === undefined || avg_talk_time_minutes === null) {
    return <NotEnoughData />;
  }
  return `${avg_talk_time_minutes.toFixed(0)} min/class`;
};

/**
 * Calculate adjusted risk level based on attendance data.
 * Rules:
 * 1. Don't use attendance for risk until student had 4 chances to attend qualifying sessions
 * 2. If student has effort/performance data:
 *    - If attendance < 70%, increase risk by one level
 *    - If attendance >= 70%, don't affect risk level
 * 3. If no effort/performance data:
 *    - High risk if < 70% attendance
 *    - Medium risk if >= 70% and < 85% attendance
 */
const calculateAdjustedRiskLevel = (
  baseRiskLevel: EngagementLevel,
  effortLevel: EngagementLevel,
  performanceLevel: EngagementLevel,
  attendanceCount: number | undefined,
  totalQualifyingSessions: number,
): EngagementLevel => {
  // If not enough qualifying sessions, return base risk (don't adjust based on attendance)
  if (totalQualifyingSessions < MIN_QUALIFYING_SESSIONS_FOR_ATTENDANCE_RISK) {
    return baseRiskLevel;
  }

  // If attendanceCount is undefined/null, treat as 0% attendance (student never attended or only attended non-qualifying sessions)
  const actualAttendanceCount = attendanceCount ?? 0;
  const attendancePercentage =
    totalQualifyingSessions > 0 ? actualAttendanceCount / totalQualifyingSessions : 0;
  const hasEffortOrPerformanceData =
    (effortLevel && effortLevel !== EngagementLevel.NONE) ||
    (performanceLevel && performanceLevel !== EngagementLevel.NONE);

  if (hasEffortOrPerformanceData) {
    // If student has effort/performance data, only increase risk if attendance < 70%
    if (attendancePercentage < ATTENDANCE_THRESHOLD_FOR_RISK_INCREASE) {
      // Increase risk by one level
      if (baseRiskLevel === EngagementLevel.LOW) {
        return EngagementLevel.MEDIUM;
      }
      if (baseRiskLevel === EngagementLevel.MEDIUM) {
        return EngagementLevel.HIGH;
      }
      // If already HIGH, stay HIGH. If NONE, it should be treated as if there was no effort/performance data
      // (in this case, it should be set to HIGH)
      return EngagementLevel.HIGH;
    }
    // If attendance >= 70%, don't affect risk level
    return baseRiskLevel;
  }
  // No effort/performance data - base risk entirely on attendance
  if (attendancePercentage < ATTENDANCE_THRESHOLD_FOR_RISK_INCREASE) {
    return EngagementLevel.HIGH;
  }
  if (attendancePercentage < ATTENDANCE_THRESHOLD_FOR_MEDIUM_RISK) {
    return EngagementLevel.MEDIUM;
  }
  // >= 85% attendance with no other data - default to LOW risk
  return EngagementLevel.LOW;
};

interface EngagementTableProps {
  dateRangeValue: number;
  engagementData: Record<string, UserEngagementDataType> | null;
}

interface EngagementRowProps {
  data: ExtendedUserEngagementDataType;
  hasVirtualSessions: boolean;
  totalSessions: number;
}

const EngagementRow: FC<EngagementRowProps> = ({ data, hasVirtualSessions, totalSessions }) => {
  return (
    <tr>
      <td>{getUserFullName(data)}</td>
      <td>{formatEffort(data)}</td>
      <td>{formatPerformance(data)}</td>
      <td>{formatAttendance(data, totalSessions)}</td>
      {hasVirtualSessions && <td>{formatTalkTime(data)}</td>}
      <td>{formatRiskLevel(data)}</td>
    </tr>
  );
};

export const EngagementTable: FC<EngagementTableProps> = ({ dateRangeValue, engagementData }) => {
  const currentSectionTitle = useAppSelector(selectCurrentSectionTitle);
  const { canAuthorSection } = useCapabilities();

  const studentUsers = useListSelector(selectUsersWhoCanParticipateInSection, PROFILE_PROPERTIES);

  const studentClassSessionUserSummaries = useAppSelector(selectStudentClassSessionUserSummaries);
  const hasVirtualSessions = useAppSelector((state) =>
    selectHasVirtualSessionsForQualifyingSessionsInDateRange(state, dateRangeValue),
  );
  const qualifyingSessionIds = useAppSelector((state) =>
    selectQualifyingSessionIdsForDateRange(state, dateRangeValue),
  );
  const totalSessions = useAppSelector((state) =>
    selectTotalQualifyingSessionsForDateRange(state, dateRangeValue),
  );

  // Compute attendance and talk time metrics based on qualifying sessions only
  const attendanceAndTalkTimeMetrics = useMemo(() => {
    if (studentClassSessionUserSummaries.length === 0) {
      return {};
    }

    const result: Record<
      string,
      { attendance_count: number; avg_talk_time_minutes: number; total_talk_time_minutes: number }
    > = {};
    const studentUserIds = new Set(studentUsers.map((user) => user.id));

    for (const userSummary of studentClassSessionUserSummaries) {
      const { user_id, class_session_id, presence_minutes, talk_time_seconds } = userSummary;
      if (qualifyingSessionIds.has(class_session_id) && studentUserIds.has(user_id)) {
        if (!result[user_id]) {
          result[user_id] = {
            attendance_count: 0,
            avg_talk_time_minutes: 0,
            total_talk_time_minutes: 0,
          };
        }
        if (isStudentPresent(presence_minutes)) {
          result[user_id].attendance_count += 1;
          result[user_id].total_talk_time_minutes += talk_time_seconds / 60.0;
          result[user_id].avg_talk_time_minutes =
            result[user_id].total_talk_time_minutes / result[user_id].attendance_count;
        }
      }
    }

    return result;
  }, [studentClassSessionUserSummaries, qualifyingSessionIds, studentUsers]);

  const extendedData: ExtendedUserEngagementDataType[] = useMemo(() => {
    if (!engagementData) {
      return [];
    }
    return studentUsers.map((user) => {
      const baseEngagementData = engagementData[user.id] || {
        effort_level: EngagementLevel.NONE,
        performance_level: EngagementLevel.NONE,
        risk_level: EngagementLevel.NONE,
      };
      const metrics = attendanceAndTalkTimeMetrics?.[user.id];

      // Calculate adjusted risk level based on attendance
      const adjustedRiskLevel = calculateAdjustedRiskLevel(
        baseEngagementData.risk_level,
        baseEngagementData.effort_level,
        baseEngagementData.performance_level,
        metrics?.attendance_count,
        totalSessions,
      );

      return {
        ...user,
        ...baseEngagementData,
        ...(metrics || {}),
        risk_level: adjustedRiskLevel,
      };
    });
  }, [studentUsers, engagementData, attendanceAndTalkTimeMetrics, totalSessions]);

  const hasEngagementData = useMemo(() => {
    return extendedData.some(
      (data) =>
        (data.effort_level && data.effort_level !== EngagementLevel.NONE) ||
        (data.performance_level && data.performance_level !== EngagementLevel.NONE),
    );
  }, [extendedData]);

  const shouldShowNotEnoughDataFooter = useMemo(() => {
    return (
      hasEngagementData &&
      extendedData.some(
        (d) =>
          d.effort_level === EngagementLevel.NONE ||
          d.performance_level === EngagementLevel.NONE ||
          d.risk_level === EngagementLevel.NONE,
      )
    );
  }, [extendedData, hasEngagementData]);

  return (
    <div className="flex flex-col gap-4">
      <SortableTableWrapper<ExtendedUserEngagementDataType, SortField>
        data={extendedData}
        defaultSortDirection="asc"
        defaultSortField="student"
        getSortValue={getSortValue}
      >
        <div className="mb-4 flex w-full items-center justify-between">
          <h3 className="h3 m-0 text-black-tint-40">{t("engagement_table.title")}</h3>
          {canAuthorSection && (
            <SortableTableExportButton<ExtendedUserEngagementDataType>
              buttonText={t("engagement_table.export.button")}
              disabled={!hasEngagementData}
              disabledMessage={t("engagement_table.export.no_data")}
              errorMessage={t("engagement_table.export.failed")}
              generateCSV={(sortedData) =>
                generateEngagementDataCSV(sortedData, currentSectionTitle || "Course")
              }
            />
          )}
        </div>

        {!hasEngagementData && (
          <EmptyCard
            description={t("engagement_table.empty_state.body")}
            icon={<ThreeBarsIcon className="text-blue" />}
            title={t("engagement_table.empty_state.header")}
          />
        )}
        {hasEngagementData && (
          <SortableTable>
            <thead>
              <tr>
                <SortableHeader
                  field="student"
                  label={t("engagement_table.columns.student.label")}
                  tooltip={t("engagement_table.columns.student.tooltip")}
                />
                <SortableHeader
                  field="effort"
                  label={t("engagement_table.columns.effort.label")}
                  tooltip={t("engagement_table.columns.effort.tooltip")}
                />
                <SortableHeader
                  field="performance"
                  label={t("engagement_table.columns.performance.label")}
                  tooltip={t("engagement_table.columns.performance.tooltip")}
                />
                <SortableHeader
                  field="attendance"
                  label={t("engagement_table.columns.attendance.label")}
                  tooltip={t("engagement_table.columns.attendance.tooltip")}
                />
                {hasVirtualSessions && (
                  <SortableHeader
                    field="talk_time"
                    label={t("engagement_table.columns.talk_time.label")}
                    tooltip={t("engagement_table.columns.talk_time.tooltip")}
                  />
                )}
                <SortableHeader
                  field="risk_level"
                  label={t("engagement_table.columns.risk_level.label")}
                  tooltip={t("engagement_table.columns.risk_level.tooltip")}
                />
              </tr>
            </thead>

            <SortableBody<ExtendedUserEngagementDataType>
              emptyColSpan={hasVirtualSessions ? 6 : 5}
              emptyMessage={t("engagement_table.no_data")}
            >
              {(sortedData) =>
                sortedData.map((userEngagementData) => (
                  <EngagementRow
                    data={userEngagementData}
                    hasVirtualSessions={hasVirtualSessions}
                    key={userEngagementData.id}
                    totalSessions={totalSessions}
                  />
                ))
              }
            </SortableBody>
          </SortableTable>
        )}
      </SortableTableWrapper>
      {shouldShowNotEnoughDataFooter && (
        <div className="flex items-center justify-start gap-1 text-black-tint-55">
          <CircleInfoIcon />
          <span className="text-xs">{t("engagement_table.not_enough_data")}</span>
        </div>
      )}
      <EngagementTableDisclaimer />
    </div>
  );
};
